// NodeTypeViewer.jsx

import React, { useMemo } from 'react';

import NodeMediaHighlight, { isNodeMediaTypeKey } from 'node/highlight/NodeMediaHighlight';
import {
  normalizeTypeViewerKey,
  resolveCanonicalTypeViewerKey,
} from 'node/highlight/nodeTypeViewerRegistry';
import PurchaseOrderViewer from 'nodetypes/purchaseorder/view/PurchaseOrderViewer';
import ProposalViewer from 'nodetypes/proposal/view/ProposalViewer';
import InvoiceViewer from 'nodetypes/invoice/view/InvoiceViewer';

const DOCUMENT_SCREENS = Object.freeze({
  PURCHASE_ORDER: PurchaseOrderViewer,
  PROPOSAL: ProposalViewer,
  INVOICE: InvoiceViewer,
});

/**
 * NodeTypeViewer
 *
 * Resolves the Highlight-slot body for registered type viewers (media + docs).
 */
const NodeTypeViewer = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  onRelatedNodeOpen = null,
}) => {
  const typeKey = useMemo(
    () => normalizeTypeViewerKey(node?.type),
    [node?.type],
  );

  const canonicalKey = useMemo(
    () => resolveCanonicalTypeViewerKey(typeKey, nodeType),
    [nodeType, typeKey],
  );

  if (!node || !typeKey) return null;

  if (isNodeMediaTypeKey(typeKey) || isNodeMediaTypeKey(canonicalKey)) {
    return <NodeMediaHighlight node={node} />;
  }

  const Screen = DOCUMENT_SCREENS[canonicalKey];
  if (!Screen) return null;

  return (
    <Screen
      node={node}
      nodeType={nodeType}
      readOnly={readOnly}
      onNodeChange={onNodeChange}
      onRelatedNodeOpen={onRelatedNodeOpen}
    />
  );
});

NodeTypeViewer.displayName = 'NodeTypeViewer';

export default NodeTypeViewer;
